'use client';

import {
  Button,
  cn,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Camera, CameraOff, ImageUp, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { triggerTapHaptic } from '@/core/lib/dnd-haptics';
import {
  AUTO_CAPTURE_SAMPLE_INTERVAL_MS,
  type AutoCaptureScanState,
  BusinessCardAutoCaptureAnalyzer,
  type CardQuad,
  isBusinessCardScanDebugEnabled,
} from '@/core/utils/business-card-auto-capture';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import {
  compressImageFile,
  compressVideoFrame,
} from '@/core/utils/compress-business-card-image';
import {
  getCameraMediaStream,
  getCameraStreamErrorMessage,
} from '@/core/utils/get-camera-media-stream';

export interface BusinessCardCaptureResult {
  imageBase64: string;
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
}

interface BusinessCardCameraModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCapture: (result: BusinessCardCaptureResult) => void;
  title: string;
  description: string;
  /** Header copy once the camera is unavailable and upload is the only way in. */
  uploadDescription: string;
  captureLabel: string;
  uploadLabel: string;
  cancelLabel: string;
  cameraErrorLabel: string;
  cameraErrorHint: string;
  cameraErrorInsecureLabel: string;
  cameraErrorPermissionLabel: string;
  cameraErrorNotFoundLabel: string;
  statusSearching: string;
  statusCardDetected: string;
  statusHoldStill: string;
}

function readFileAsCapture(file: File): Promise<BusinessCardCaptureResult> {
  return compressImageFile(file);
}

const GUIDE_BORDER: Record<AutoCaptureScanState, string> = {
  searching: 'border-primary/50',
  cardDetected: 'border-amber-500',
  holdStill: 'border-green-500',
};

export function BusinessCardCameraModal({
  open,
  onOpenChange,
  onCapture,
  title,
  description,
  uploadDescription,
  captureLabel,
  uploadLabel,
  cancelLabel,
  cameraErrorLabel,
  cameraErrorHint,
  cameraErrorInsecureLabel,
  cameraErrorPermissionLabel,
  cameraErrorNotFoundLabel,
  statusSearching,
  statusCardDetected,
  statusHoldStill,
}: BusinessCardCameraModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const analyzerRef = useRef<BusinessCardAutoCaptureAnalyzer | null>(null);
  const analysisCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const captureLockRef = useRef(false);

  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [scanState, setScanState] = useState<AutoCaptureScanState>('searching');
  const [stableProgress, setStableProgress] = useState(0);
  const [cardQuad, setCardQuad] = useState<CardQuad | null>(null);
  const [showFlash, setShowFlash] = useState(false);

  const stopCamera = useCallback(() => {
    const stream = streamRef.current;
    streamRef.current = null;
    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const resetScanState = useCallback(() => {
    analyzerRef.current?.reset();
    setScanState('searching');
    setStableProgress(0);
    setCardQuad(null);
    captureLockRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    resetScanState();
    setCameraError(null);
    setIsCapturing(false);
    setShowFlash(false);
    onOpenChange(false);
  }, [onOpenChange, resetScanState, stopCamera]);

  const performCapture = useCallback(async () => {
    if (captureLockRef.current) return;

    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;

    captureLockRef.current = true;
    setIsCapturing(true);
    setShowFlash(true);

    void triggerTapHaptic();

    window.setTimeout(() => {
      setShowFlash(false);
    }, 160);

    try {
      const compressed = compressVideoFrame(video);
      if (isBusinessCardScanDebugEnabled()) {
        console.debug('[card-scan] capture triggered (auto or manual)');
      }
      stopCamera();
      onCapture(compressed);
    } finally {
      setIsCapturing(false);
    }
  }, [onCapture, stopCamera]);

  const [prevOpen, setPrevOpen] = useState(open);
  if (!open && open !== prevOpen) {
    setPrevOpen(open);
    setScanState('searching');
    setStableProgress(0);
    setCardQuad(null);
    setCameraError(null);
  } else if (open && open !== prevOpen) {
    setPrevOpen(open);
  }

  useEffect(() => {
    if (!open) {
      analyzerRef.current?.reset();
      captureLockRef.current = false;
      stopCamera();
      return;
    }

    let cancelled = false;

    void (async () => {
      if (isBusinessCardScanDebugEnabled()) {
        console.debug(
          '[card-scan] debug logging enabled (VITE_DEV_DEBUG=true). Metrics log ~1/s and on state changes.',
        );
      }

      const startDelayMs = isCapacitorNative() ? 0 : 150;
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, startDelayMs);
      });
      if (cancelled) return;

      try {
        const stream = await getCameraMediaStream();
        if (cancelled) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.setAttribute('playsinline', 'true');
          videoRef.current.setAttribute('webkit-playsinline', 'true');
          void videoRef.current.play().catch(() => {});
        }
      } catch (err: unknown) {
        if (!cancelled) {
          if (isBusinessCardScanDebugEnabled()) {
            console.debug('[card-scan] camera start failed', err);
          }
          setCameraError(
            getCameraStreamErrorMessage(err, {
              default: cameraErrorLabel,
              insecureContext: cameraErrorInsecureLabel,
              permissionDenied: cameraErrorPermissionLabel,
              notFound: cameraErrorNotFoundLabel,
            }),
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [
    open,
    stopCamera,
    cameraErrorLabel,
    cameraErrorInsecureLabel,
    cameraErrorPermissionLabel,
    cameraErrorNotFoundLabel,
  ]);

  useEffect(() => {
    if (!open || cameraError || isCapturing) {
      return;
    }

    if (!analyzerRef.current) {
      analyzerRef.current = new BusinessCardAutoCaptureAnalyzer();
    }
    if (!analysisCanvasRef.current) {
      analysisCanvasRef.current = document.createElement('canvas');
    }

    const analyzer = analyzerRef.current;
    const canvas = analysisCanvasRef.current;

    const intervalId = window.setInterval(() => {
      const video = videoRef.current;
      if (!video?.videoWidth || captureLockRef.current) {
        return;
      }

      const result = analyzer.analyze(video, canvas, performance.now());
      setScanState(result.state);
      setStableProgress(result.stableProgress);
      setCardQuad(result.quad);

      if (result.shouldCapture) {
        void performCapture();
      }
    }, AUTO_CAPTURE_SAMPLE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      analyzer.reset();
    };
  }, [open, cameraError, isCapturing, performCapture]);

  const handleManualCapture = () => {
    void performCapture();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const result = await readFileAsCapture(file);
      stopCamera();
      onCapture(result);
    } catch {
      setCameraError(cameraErrorLabel);
    }
  };

  const statusLabel =
    scanState === 'holdStill'
      ? statusHoldStill
      : scanState === 'cardDetected'
        ? statusCardDetected
        : statusSearching;

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
        else onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="flex max-h-[90vh] w-full flex-col gap-4 p-0 sm:max-w-md">
        <ResponsiveDialogHeader className="space-y-1 px-6 pt-6">
          <ResponsiveDialogTitle className="text-lg font-semibold">
            {title}
          </ResponsiveDialogTitle>
          <p className="text-sm text-muted-foreground">
            {cameraError ? uploadDescription : description}
          </p>
        </ResponsiveDialogHeader>

        <div className="flex flex-col gap-3 px-6">
          {cameraError ? (
            <div
              className="flex items-start gap-3 rounded-lg border bg-muted/50 p-4"
              role="status"
            >
              <CameraOff
                className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium">{cameraErrorLabel}</p>
                <p className="text-xs text-balance break-words text-muted-foreground">
                  {cameraError === cameraErrorLabel
                    ? cameraErrorHint
                    : cameraError}
                </p>
              </div>
            </div>
          ) : null}
          {/* Stays mounted (hidden) without a camera so the video ref survives. */}
          <div
            className={cn(
              'relative w-full overflow-hidden rounded-lg bg-muted',
              cameraError && 'hidden',
            )}
            style={{
              aspectRatio: '1.586',
              minHeight: 180,
              maxHeight: 'min(55vmin, 280px)',
            }}
          >
            {/* The preview reports tabIndex 0 but cannot take focus; the
                dialog's Tab loop wrapped onto it and left focus stuck on X
                (DEV-540). */}
            <video
              ref={videoRef}
              playsInline
              muted
              tabIndex={-1}
              aria-hidden
              className="absolute inset-0 size-full object-cover"
            />
            <div
              className={cn(
                'pointer-events-none absolute inset-4 rounded-lg border-2 border-dashed transition-colors duration-200',
                GUIDE_BORDER[scanState],
              )}
            />
            {cardQuad ? (
              <svg
                className="pointer-events-none absolute inset-0 size-full"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                aria-hidden
              >
                <polygon
                  points={cardQuad
                    .map((point) => `${point.x},${point.y}`)
                    .join(' ')}
                  className={cn(
                    'transition-[stroke] duration-200',
                    scanState === 'holdStill'
                      ? 'fill-green-500/15 stroke-green-500'
                      : 'fill-amber-500/15 stroke-amber-500',
                  )}
                  strokeWidth={3}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            ) : null}
            {scanState === 'holdStill' && stableProgress > 0 ? (
              <div className="pointer-events-none absolute inset-x-4 bottom-4 h-1 overflow-hidden rounded-full bg-background/40">
                <div
                  className="h-full rounded-full bg-green-500 transition-[width] duration-100 ease-linear"
                  style={{ width: `${Math.round(stableProgress * 100)}%` }}
                />
              </div>
            ) : null}
            {showFlash ? (
              <div className="pointer-events-none absolute inset-0 bg-white/75 transition-opacity duration-150" />
            ) : null}
            {!cameraError ? (
              <div className="pointer-events-none absolute inset-x-0 top-3 flex justify-center px-4">
                <span
                  className={cn(
                    'rounded-full px-3 py-1 text-xs font-medium shadow-sm backdrop-blur-sm',
                    scanState === 'holdStill'
                      ? 'bg-green-500/90 text-white'
                      : scanState === 'cardDetected'
                        ? 'bg-amber-500/90 text-white'
                        : 'bg-background/80 text-foreground',
                  )}
                >
                  {statusLabel}
                </span>
              </div>
            ) : null}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => void handleFileChange(e)}
          />
        </div>

        <ResponsiveDialogFooter className="flex-col gap-2 border-t px-6 py-4 sm:flex-col">
          <Button
            type="button"
            variant={cameraError ? 'primary' : 'outline'}
            className="w-full gap-2"
            onClick={() => fileInputRef.current?.click()}
            disabled={isCapturing}
          >
            <ImageUp className="h-4 w-4" />
            {uploadLabel}
          </Button>
          {cameraError ? null : (
            <Button
              type="button"
              variant="ghost"
              className="w-full gap-2"
              onClick={() => void handleManualCapture()}
              disabled={isCapturing}
            >
              {isCapturing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
              {captureLabel}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={handleClose}
            disabled={isCapturing}
          >
            {cancelLabel}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
